import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { usePostvalidate } from '@/hooks/usePatching'
import { toPatchApiError } from '@/services/patching'
import type { PatchEnvironment, PostPatchCheck } from '@/types/patching'
import styles from './Patching.module.css'

interface PostPatchPanelProps {
  campaignId: string
  env: PatchEnvironment
  checks: PostPatchCheck[]
}

/** Runs an Azure assessment after the install. It does not install patches. */
export function PostPatchPanel({ campaignId, env, checks }: PostPatchPanelProps) {
  const run = usePostvalidate()
  const [override, setOverride] = useState<PostPatchCheck[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const signature = checks.map((row) => `${row.check}:${row.status}:${row.detail}`).join('|')
  useEffect(() => {
    setOverride(null)
  }, [signature])
  const rows = override ?? checks

  async function onRun() {
    setError(null)
    try {
      setOverride(await run.mutateAsync({ campaignId, env }))
    } catch (err) {
      setError(toPatchApiError(err).message)
    }
  }

  return (
    <div className={styles.stack} data-testid="postpatch-panel">
      <p className={styles.muted}>
        Runs an Azure assessment after the install. This does not install patches. Acceptance stays blocked until every
        mandatory check passes.
      </p>
      <div className={styles.actionRow}>
        <Button variant="secondary" onClick={onRun} loading={run.isPending} disabled={run.isPending}>
          Run post-patch validation
        </Button>
      </div>
      {error && (
        <div className={clsx(styles.alert, styles.alertError)} role="alert">
          {error}
        </div>
      )}
      {rows.length === 0 && <p className={styles.muted}>Post-patch validation has not run yet.</p>}
      {rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Check</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.check}-${row.status}`} data-testid="postpatch-row">
                  <td>{row.check}{row.mandatory ? '' : ' (optional)'}</td>
                  <td>{row.status}</td>
                  <td>{row.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
